// ONE segment taxonomy for the whole app.
//
// There used to be three overlapping lists: SHARE_SEGMENTS (with a 'Total' member),
// SOLD_SEGMENTS (with an 'Alla' member) and a display map in sections.tsx that rendered
// Tobacco as "Tobakssnus" — so the same segment read as "Tobak" in one filter and
// "Tobakssnus" in the chart beside it, and the two "no narrowing" members were spelled
// differently while meaning the same thing (C16).
//
// One internal enum, one display string per segment, one API value.

import { displayLabel } from './displayLabels'

export type Segment = 'ALL' | 'ALL_WHITE' | 'TOBACCO' | 'VAPE' | 'NICOTINE_FREE' | 'SMOKING' | 'OTHER'

// Left-to-right order in every segment picker: the three snus segments first, then vape,
// then smoking — so a button strip never places vape/smoking in the middle of the snus
// segments (Olof). Smoking is brand-classified (#4), not a catalog segment.
export const SEGMENTS: readonly Segment[] = ['ALL', 'ALL_WHITE', 'TOBACCO', 'NICOTINE_FREE', 'VAPE', 'SMOKING', 'OTHER']

// Product-area grouping for the segment pickers (Olof: "nu hamnar den lite random med vape
// under snus"). Snus segments group together, vape stands apart — rendered as <optgroup>s so
// the filter can never read vape as a snus segment. Smoking has no snus-style segment enum
// (its SKUs carry no catalog segment), so it isn't a filter option here.
export const SEGMENT_AREAS: { area: string; segments: Exclude<Segment, 'ALL'>[] }[] = [
  { area: 'Snus', segments: ['ALL_WHITE', 'TOBACCO', 'NICOTINE_FREE'] },
  { area: 'Vape', segments: ['VAPE'] },
  { area: 'Smoking', segments: ['SMOKING'] },
  { area: 'Other', segments: ['OTHER'] },
]

// Olof's top level: the three product areas + the Övrigt slask. Snus counts in CU,
// vape/smoking too (Block 9); Övrigt is snus-form. `key` is the backend `area` filter value.
export type Area = 'snus' | 'vape' | 'smoking' | 'other'
export const AREA_LABEL: Record<Area, string> = {
  snus: 'Snus',
  vape: 'Vape',
  smoking: 'Smoking',
  other: 'Other',
}
// Block 9: ONE unit across the whole dashboard — CU (Customer Unit, minsta säljbara enhet). The
// headline already sums every area in CU, so the per-area blocks must not call vape/smoking
// "units" (that read as a different measure). A vape pod pack / cigarette pack is one CU too.
export const AREA_UNIT: Record<Area, string> = {
  snus: 'CU',
  vape: 'CU',
  smoking: 'CU',
  other: 'CU',
}

// One accent colour per product area (Olof: "sära på dem med någon färg så du vet vilket
// block du är i"). Used for the block heading + a left rule on each area section.
export const AREA_COLOR: Record<Area, string> = {
  snus: '#0f4c52', // teal — the brand colour, snus is the core
  vape: '#5b6cc4', // indigo
  smoking: '#b5642a', // amber/brown
  other: '#7a8788', // muted grey — the slask
}
export const AREA_ORDER: readonly Area[] = ['snus', 'vape', 'smoking', 'other']

// The sub-segments a per-area block can drill into (Olof still wants All White / Tobacco /
// Nicotine-free inside the Snus block). Areas with no enum sub-segments (smoking/other) or a
// single one (vape) show no sub-picker.
export const AREA_SEGMENTS: Record<Area, Exclude<Segment, 'ALL'>[]> = {
  snus: ['ALL_WHITE', 'TOBACCO', 'NICOTINE_FREE'],
  vape: ['VAPE'],
  smoking: [],
  other: [],
}

// Vape/Smoking sub-categories drill on the backend LEAF (not a snus-style Segment enum): the
// `value` is the exact _segment_detail leaf the sku-list `leaf` param filters on. Snus/other
// have none here (snus uses AREA_SEGMENTS above).
export const AREA_LEAVES: Record<Area, { value: string; label: string }[]> = {
  snus: [],
  vape: [
    { value: 'Vape pod', label: 'Pod' },
    { value: 'Disposable vape', label: 'Disposable' },
  ],
  smoking: [
    { value: 'Cigarettes', label: 'Cigarettes' },
    { value: 'Heat-not-burn', label: 'Heat-not-burn' },
  ],
  other: [],
}

/** Backend segment-view category ('Snus'/'Vape'/'Smoking'/'Other') → the Area key. */
export function areaFromCategory(category: string): Area | null {
  const c = category.trim().toLowerCase()
  return c === 'snus' || c === 'vape' || c === 'smoking' || c === 'other' ? (c as Area) : null
}

// The label the backend filters on. It canonicalises these (main._SHARE_SEGMENTS), so the
// English spelling is the contract regardless of what the UI displays. 'ALL' means "no
// segment narrowing" and therefore carries no filter value at all.
const API_VALUE: Record<Exclude<Segment, 'ALL'>, string> = {
  ALL_WHITE: 'All White',
  TOBACCO: 'Tobacco',
  VAPE: 'Vape',
  NICOTINE_FREE: 'Nicotine Free',
  SMOKING: 'Smoking',
  OTHER: 'Other',
}

// Display strings. English (A1) — the manufacturers are international, so Swedish-only
// chrome was a mistake. One string per segment, used in every view, legend and caption.
// "Tobacco" alone is ambiguous (tobacco snus vs cigarettes) — Olof: "tobakko är alltså
// tobakko snus". This segment is always tobacco SNUS, so the label says so everywhere.
const LABEL: Record<Segment, string> = {
  ALL: 'All',
  ALL_WHITE: 'All White',
  TOBACCO: 'Tobacco snus',
  VAPE: 'Vape',
  NICOTINE_FREE: 'Nicotine-free',
  SMOKING: 'Smoking',
  OTHER: 'Other',
}

/** The filter value to send, or undefined for "no segment narrowing". */
export function segmentApiValue(seg: Segment): string | undefined {
  return seg === 'ALL' ? undefined : API_VALUE[seg]
}

/** The one display string for a segment. */
export function segmentLabel(seg: Segment): string {
  return LABEL[seg]
}

/** What a share is "andel av" — 'ALL' is the whole market, not a segment. */
export function segmentDenominatorLabel(seg: Segment): string {
  return seg === 'ALL' ? 'the whole market' : LABEL[seg]
}

/** Resolve a backend/catalog segment label to the enum. Unknown → null (never guessed). */
export function segmentFromApi(raw: string | null | undefined): Segment | null {
  const key = (raw ?? '').trim().toLowerCase()
  if (!key) return null
  for (const seg of SEGMENTS) {
    if (seg === 'ALL') continue
    if (API_VALUE[seg].toLowerCase() === key) return seg
  }
  // The variants the catalog and the older UI used for the same segments.
  const ALIAS: Record<string, Segment> = {
    tobak: 'TOBACCO',
    tobakssnus: 'TOBACCO',
    nikotinfritt: 'NICOTINE_FREE',
    nikotinfri: 'NICOTINE_FREE',
    'nicotine-free': 'NICOTINE_FREE',
    allwhite: 'ALL_WHITE',
    'all-white': 'ALL_WHITE',
  }
  return ALIAS[key] ?? null
}

/**
 * Display label for a raw segment label coming from the catalog. Known segments get the
 * canonical string; the catalog's finer labels (Portionssnus, Engångsvape, Övrigt …) get their
 * English label from the shared taxonomy map rather than being bucketed into a wrong segment,
 * and a value the map does not know passes through unchanged (MP-OS-191 #13).
 */
export function displaySegment(raw: string): string {
  const seg = segmentFromApi(raw)
  return seg ? LABEL[seg] : displayLabel(raw)
}
